import { useEffect, useRef, useState } from "react"

/** Values returned by useAutoRefresh for driving the toggle UI. */
interface UseAutoRefreshResult {
  /** Whether auto-refresh is currently enabled. */
  autoRefresh: boolean
  /** Enables or disables auto-refresh. */
  setAutoRefresh: (enabled: boolean) => void
  /** Seconds remaining until the next refresh, for display next to the toggle. */
  countdown: number
}

/**
 * Runs a refresh callback on a countdown while the tab is visible.
 *
 * Refreshing a hidden tab wastes API calls, so when the countdown expires in a
 * background tab the refresh is deferred and fires as soon as the tab becomes
 * visible again. That keeps a long-running job page from hammering the HPC
 * account while nobody is watching it, and still shows fresh data the moment
 * the user returns.
 *
 * @param onRefresh - Called each time a refresh is due. Should be stable
 *   (memoised with useCallback), since changing it restarts the countdown.
 * @param intervalSeconds - Seconds between refreshes.
 * @returns The auto-refresh toggle state, its setter, and the countdown value.
 */
export function useAutoRefresh({
  onRefresh,
  intervalSeconds,
}: {
  onRefresh: () => void
  intervalSeconds: number
}): UseAutoRefreshResult {
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false)
  const [countdown, setCountdown] = useState<number>(intervalSeconds)

  // Set when a refresh came due while the tab was hidden, so it can be run on
  // the user's return instead of being skipped.
  const pendingRefresh = useRef<boolean>(false)

  useEffect(() => {
    if (!autoRefresh) {
      setCountdown(intervalSeconds)
      return
    }

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible" && pendingRefresh.current) {
        pendingRefresh.current = false
        onRefresh()
        setCountdown(intervalSeconds)
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    const interval = setInterval(() => {
      setCountdown((remaining) => {
        if (remaining > 1) {
          return remaining - 1
        }

        // Countdown expired: refresh now if the user can see the result,
        // otherwise hold at zero and defer until the tab is visible again.
        if (document.visibilityState === "visible") {
          onRefresh()
          return intervalSeconds
        }

        pendingRefresh.current = true
        return 0
      })
    }, 1000)

    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [autoRefresh, intervalSeconds, onRefresh])

  return { autoRefresh, setAutoRefresh, countdown }
}
